import {
  Box,
  ListItem,
  ListItemIcon,
  RadioGroup,
  Radio,
  ListItemText,
  Typography,
  makeStyles,
} from "@material-ui/core";
import BoltIcon from "@material-ui/icons/FlashOn";
import { Field, useFormikContext } from "formik";
import React from "react";
import InstallationTermsModal from "components/funnel/steps/funnelStepCart/shippingOptions/InstallationTermsModal";
import useCart from "../../../../../hooks/useCart";
import useDeliveryOptions from "hooks/useDeliveryOptions.js";
import { formatPriceCents } from "../../../../product/utils";
import { SHIPPING_OPTIONS, INSTALLATION, CLICK_AND_COLLECT } from "../../../constants";
import theme from "theme";
import HelpTextCard from "components/design-system/molecules/HelpTextCard";
import { getCartEligibility } from "components/localOfferEligibility/LocalOfferEligibility";
import AddressOneLiner from "components/design-system/atoms/Address";

interface FormikValue {
  delivery_option_type: string;
  delivery_option_id: string;
  date: string;
  slot: string;
}

interface ProductDeliveryChoiceProps {
  product: any;
}

const ProductDeliveryChoice: React.FunctionComponent<ProductDeliveryChoiceProps> = ({ product }) => {
  const classes = useStyle();
  const formik = useFormikContext<FormikValue>();
  const delivery_options = useDeliveryOptions(product.id, product.product_type_slug);
  const [cart, dispatch] = useCart();
  const [installationModalOpen, setInstallationModalOpen] = React.useState(false);

  const isOptionSelected = (option) => {
    return formik.values["delivery_option_id"] === option.id;
  };

  React.useEffect(() => {
    if (delivery_options && formik.values.delivery_option_id) {
      const option = delivery_options.find((x) => x.id === formik.values.delivery_option_id);
      if (option) {
        dispatch({
          type: "updateShippingOptions",
          product,
          delivery_option_id: option.id,
          delivery_option_type: option.type,
          shipping_price: option.price,
        });
        dispatch({
          type: "updateShipping",
          shipping: {
            local: getCartEligibility(cart),
            recoveryEnabled: !(option.collection_available === false),
            recoverOldDevice: option.collection_available && cart.shipping.recoverOldDevice,
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delivery_options, formik.values.delivery_option_id, dispatch, product]);

  return (
    <>
      <Box position="relative">
        <Box
          px={2}
          position="absolute"
          zIndex="10"
          left="30%"
          top="-12px"
          color="#3E3E3E"
          style={{ backgroundColor: theme.palette.grey200.main }}
        >
          <Typography variant="body2">Choisissez votre option</Typography>
        </Box>
      </Box>
      {cart.selection.length > 1 && formik.values.delivery_option_type && (
        <HelpTextCard whiteBackground mt={3}>
          Vous devez choisir le même mode de livraison pour les {cart.selection.length} appareils
        </HelpTextCard>
      )}
      <Field component={RadioGroup} name="delivery_option_id" className={classes.radioGroup}>
        <div className={classes.shippingOptionsContainer}>
          <RadioGroup value={formik.values.delivery_option_id}>
            {delivery_options &&
              delivery_options.map((option, index) => (
                <Box key={index} py={1} className={index !== 0 ? classes.borderTop : ""}>
                  <ListItem
                    key={option.id}
                    id="method"
                    disableRipple
                    button
                    onClick={() => {
                      setInstallationModalOpen(option.type === "installation");
                      formik.setValues({
                        delivery_option_type: option.type,
                        delivery_option_id: option.id,
                        date: "",
                        slot: "",
                      });
                    }}
                    className={classes.listItem}
                  >
                    <ListItemIcon>
                      <Radio
                        className={classes.radio}
                        color="primary"
                        disableRipple
                        checked={formik.values.delivery_option_id === option.id}
                      />
                    </ListItemIcon>
                    <ListItemText
                      className={classes.itemText}
                      primary={
                        <>
                          <Typography variant="body2" className={isOptionSelected(option) ? classes.bold : ""}>
                            {SHIPPING_OPTIONS.find((x) => x.type == option.type).titleMin}
                            {!option.collection_available && " sans reprise"}
                          </Typography>
                          {!option.collection_available && (
                            <Box display="flex">
                              <BoltIcon />
                              <Typography color="secondary" variant="caption" className={classes.bold}>
                                Livraison express en 2 à 5 jours ouvrés
                              </Typography>
                            </Box>
                          )}
                          {option.type === CLICK_AND_COLLECT && <AddressOneLiner address={product.workshop_address} />}
                        </>
                      }
                    />
                    {option.type === INSTALLATION && installationModalOpen && <InstallationTermsModal />}
                    <Typography
                      component="span"
                      variant="body2"
                      className={`${classes.price} ${isOptionSelected(option) ? classes.bold : ""}`}
                    >
                      {option.price === 0 ? "Offert" : formatPriceCents(option.price)}
                    </Typography>
                  </ListItem>
                </Box>
              ))}
          </RadioGroup>
        </div>
      </Field>
      <Typography variant="body2" color="error">
        {formik.errors["delivery_option_type"]}
      </Typography>
    </>
  );
};

const useStyle = makeStyles((theme) => ({
  borderTop: {
    borderTop: "1px solid #DBDFE8",
  },
  shippingOptionsContainer: {
    color: theme.palette.secondary.dark,
    display: "grid",
    gridGap: theme.spacing(1),
    position: "relative",
    alignItems: "flex-start",
    marginTop: theme.spacing(3),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      "& > :last-child": {
        marginTop: theme.spacing(),
      },
    },
  },
  bold: {
    fontWeight: 500,
  },
  itemText: {
    alignSelf: "center",
    marginRight: theme.spacing(1),
  },
  radioGroup: { flexWrap: "unset" },
  radio: {
    marginRight: 5,
  },
  listItem: {
    alignItems: "center",
    "& .MuiListItemIcon-root": {
      minWidth: 0,
    },
  },
  price: {
    marginLeft: "auto",
    alignSelf: "center",
  },
}));

export default ProductDeliveryChoice;
